import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import {
  PlusIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
  QrCodeIcon,
  ExclamationTriangleIcon,
  CameraIcon,
  Squares2X2Icon,
  ListBulletIcon,
} from '@heroicons/react/24/outline'
import { productService } from '../../services/productService'
import toast from 'react-hot-toast'

interface Product {
  product_id: number
  sku: string
  barcode: string | null
  product_name: string
  description: string | null
  warranty_months: number
  track_serial_numbers: boolean
  reorder_level: number
  brands: { brand_name: string } | null
  categories: { category_name: string } | null
  product_images: { image_url: string }[]
  _count: { serial_numbers: number }
}

export const ProductsPage = () => {
  const [search, setSearch] = useState('')
  const [showFilters, setShowFilters] = useState(false)
  const [page, setPage] = useState(1)
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('list')

  const { data, isLoading, error } = useQuery({
    queryKey: ['products', search, page],
    queryFn: () => productService.getAll({ search, page, limit: 20 }),
  })

  const { data: lowStockData } = useQuery({
    queryKey: ['products', 'low-stock'],
    queryFn: () => productService.getLowStock(),
  })

  const products: Product[] = data?.data?.data?.items || []
  const pagination = data?.data?.data?.pagination
  const lowStockCount = lowStockData?.data?.data?.length || 0

  if (error) {
    toast.error('Failed to load products')
  }

  const renderGridView = () => (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
      {products.map((product) => (
        <Link
          key={product.product_id}
          to={`/products/${product.product_id}`}
          className="bg-white rounded-xl shadow border border-gray-100 overflow-hidden hover:shadow-lg transition-shadow group"
        >
          {/* Product Image */}
          <div className="aspect-square bg-gray-100 relative">
            {product.product_images[0] ? (
              <img
                src={product.product_images[0].image_url}
                alt={product.product_name}
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center">
                <CameraIcon className="w-16 h-16 text-gray-300" />
              </div>
            )}
            {product.track_serial_numbers && product._count.serial_numbers <= product.reorder_level && (
              <div className="absolute top-2 right-2 bg-yellow-500 text-white text-xs font-medium px-2 py-1 rounded">
                Low Stock
              </div>
            )}
            {product.track_serial_numbers && product._count.serial_numbers === 0 && (
              <div className="absolute top-2 right-2 bg-red-500 text-white text-xs font-medium px-2 py-1 rounded">
                Out of Stock
              </div>
            )}
          </div>

          {/* Product Info */}
          <div className="p-4">
            <div className="flex items-start justify-between gap-2">
              <div>
                <p className="text-xs text-gray-500 mb-1">{product.brands?.brand_name}</p>
                <h3 className="font-medium text-gray-900 line-clamp-2 group-hover:text-primary-600">
                  {product.product_name}
                </h3>
              </div>
            </div>

            <div className="mt-3 flex items-center justify-between text-sm">
              <span className="text-gray-500">SKU: {product.sku}</span>
              {product.track_serial_numbers ? (
                <span className={`font-medium ${
                  product._count.serial_numbers <= product.reorder_level
                    ? 'text-yellow-600'
                    : 'text-green-600'
                }`}>
                  {product._count.serial_numbers} in stock
                </span>
              ) : (
                <span className="text-gray-400">Non-tracked</span>
              )}
            </div>

            {product.warranty_months > 0 && (
              <p className="mt-2 text-xs text-gray-500">
                {product.warranty_months} months warranty
              </p>
            )}
          </div>
        </Link>
      ))}
    </div>
  )

  const renderListView = () => (
    <div className="bg-white rounded-xl shadow border border-gray-100 overflow-hidden">
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Product
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                SKU
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Brand
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Category
              </th>
              <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                Stock
              </th>
              <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                Status
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {products.map((product) => (
              <tr
                key={product.product_id}
                className="hover:bg-gray-50 cursor-pointer"
              >
                <td className="px-4 py-3">
                  <Link
                    to={`/products/${product.product_id}`}
                    className="block"
                  >
                    <p className="text-sm font-medium text-gray-900 hover:text-primary-600">
                      {product.product_name}
                    </p>
                    {product.warranty_months > 0 && (
                      <p className="text-xs text-gray-500 mt-0.5">
                        {product.warranty_months} months warranty
                      </p>
                    )}
                  </Link>
                </td>
                <td className="px-4 py-3 text-sm text-gray-600">
                  {product.sku}
                </td>
                <td className="px-4 py-3 text-sm text-gray-600">
                  {product.brands?.brand_name || '-'}
                </td>
                <td className="px-4 py-3 text-sm text-gray-600">
                  {product.categories?.category_name || '-'}
                </td>
                <td className="px-4 py-3 text-sm text-right">
                  {product.track_serial_numbers ? (
                    <span className={`font-medium ${
                      product._count.serial_numbers === 0
                        ? 'text-red-600'
                        : product._count.serial_numbers <= product.reorder_level
                        ? 'text-yellow-600'
                        : 'text-green-600'
                    }`}>
                      {product._count.serial_numbers}
                    </span>
                  ) : (
                    <span className="text-gray-400">—</span>
                  )}
                </td>
                <td className="px-4 py-3 text-center">
                  {product.track_serial_numbers ? (
                    <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                      product._count.serial_numbers === 0
                        ? 'bg-red-100 text-red-800'
                        : product._count.serial_numbers <= product.reorder_level
                        ? 'bg-yellow-100 text-yellow-800'
                        : 'bg-green-100 text-green-800'
                    }`}>
                      {product._count.serial_numbers === 0
                        ? 'Out of Stock'
                        : product._count.serial_numbers <= product.reorder_level
                        ? 'Low Stock'
                        : 'In Stock'}
                    </span>
                  ) : (
                    <span className="inline-flex px-2 py-1 text-xs font-medium rounded-full bg-gray-100 text-gray-600">
                      Non-tracked
                    </span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Products</h1>
          <p className="text-sm text-gray-500 mt-1">
            Manage your camera equipment and accessories
          </p>
        </div>
        <div className="flex items-center gap-3">
          {lowStockCount > 0 && (
            <Link
              to="/inventory?filter=low-stock"
              className="inline-flex items-center gap-2 px-4 py-2 bg-yellow-50 text-yellow-700 rounded-lg hover:bg-yellow-100"
            >
              <ExclamationTriangleIcon className="w-5 h-5" />
              <span className="text-sm font-medium">{lowStockCount} Low Stock</span>
            </Link>
          )}
          <Link
            to="/products/new"
            className="inline-flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
          >
            <PlusIcon className="w-5 h-5" />
            Add Product
          </Link>
        </div>
      </div>

      {/* Search, Filters, and View Toggle */}
      <div className="bg-white rounded-xl shadow border border-gray-100 p-4">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1 relative">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search by name, SKU, or barcode..."
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(1) }}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="inline-flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              <FunnelIcon className="w-5 h-5" />
              Filters
            </button>
            <button className="inline-flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50">
              <QrCodeIcon className="w-5 h-5" />
              Scan
            </button>
            {/* View Toggle */}
            <div className="flex items-center border border-gray-300 rounded-lg overflow-hidden">
              <button
                onClick={() => setViewMode('grid')}
                className={`inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium ${
                  viewMode === 'grid'
                    ? 'bg-primary-50 text-primary-700'
                    : 'bg-white text-gray-600 hover:bg-gray-50'
                }`}
                title="Grid view"
              >
                <Squares2X2Icon className="w-4 h-4" />
                <span className="hidden sm:inline">Grid</span>
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium border-l border-gray-300 ${
                  viewMode === 'list'
                    ? 'bg-primary-50 text-primary-700'
                    : 'bg-white text-gray-600 hover:bg-gray-50'
                }`}
                title="List view"
              >
                <ListBulletIcon className="w-4 h-4" />
                <span className="hidden sm:inline">List</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Products Content */}
      {isLoading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
        </div>
      ) : products.length === 0 ? (
        <div className="bg-white rounded-xl shadow border border-gray-100 p-12 text-center">
          <CameraIcon className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-1">No products found</h3>
          <p className="text-gray-500 mb-4">
            {search ? 'Try adjusting your search terms' : 'Get started by adding your first product'}
          </p>
          {!search && (
            <Link
              to="/products/new"
              className="inline-flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
            >
              <PlusIcon className="w-5 h-5" />
              Add Product
            </Link>
          )}
        </div>
      ) : (
        <>
          {viewMode === 'grid' ? renderGridView() : renderListView()}

          {/* Pagination */}
          {pagination && pagination.pages > 1 && (
            <div className="flex items-center justify-between bg-white rounded-xl shadow border border-gray-100 p-4">
              <p className="text-sm text-gray-500">
                Showing {(pagination.page - 1) * pagination.limit + 1} to{' '}
                {Math.min(pagination.page * pagination.limit, pagination.total)} of{' '}
                {pagination.total} products
              </p>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className="px-3 py-1 border border-gray-300 rounded-lg disabled:opacity-50 hover:bg-gray-50"
                >
                  Previous
                </button>
                <span className="text-sm text-gray-600">
                  Page {pagination.page} of {pagination.pages}
                </span>
                <button
                  onClick={() => setPage(p => Math.min(pagination.pages, p + 1))}
                  disabled={page === pagination.pages}
                  className="px-3 py-1 border border-gray-300 rounded-lg disabled:opacity-50 hover:bg-gray-50"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  )
}
